import { useMemo } from 'react'

import { Course } from '@mc/api'
import { useTranslation } from '@mc/i18n'
import { TrackingContainer } from '@mc/visibility'

import { GlobalCarousel } from 'components/GlobalCarousel'
import { LOCATION_RELATED_COURSES } from 'components/GlobalCarousel/events'
import {
  useCoursesBySlugs as useGetCoursesBySlugs,
  useRelatedCoursesByAffinity,
} from 'libs/client/api'

import { MEMBERS_ALSO_LIKED_EXP_I18N_NAMESPACE } from './i18n'
import { trackRelatedCoursesCarouselViewed } from './events'

type MembersAlsoLikedVariant1 = {
  courseSlug: string
  fallbackRelatedCourses: Course[]
}

export const MembersAlsoLikedVariant1 = ({
  courseSlug,
  fallbackRelatedCourses,
}: MembersAlsoLikedVariant1) => {
  const { t } = useTranslation(MEMBERS_ALSO_LIKED_EXP_I18N_NAMESPACE)

  const {
    getRelatedCoursesByAffinity,
    error: relatedError,
    isLoading: relatedLoading,
  } = useRelatedCoursesByAffinity()

  const relatedSlugs = useMemo(() => {
    if (relatedLoading) return [] // Don't compute until data is available
    return getRelatedCoursesByAffinity(courseSlug)?.slice(0, 6) ?? []
  }, [courseSlug, getRelatedCoursesByAffinity, relatedLoading])

  const {
    coursesBySlugs,
    isLoading: coursesLoading,
    error: coursesError,
  } = useGetCoursesBySlugs(relatedSlugs)

  const relatedCourses: Course[] = useMemo(() => {
    if (!coursesBySlugs || !relatedSlugs.length) return fallbackRelatedCourses

    return [...coursesBySlugs].sort(
      (a, b) => relatedSlugs.indexOf(a.slug) - relatedSlugs.indexOf(b.slug),
    )
  }, [coursesBySlugs, relatedSlugs, fallbackRelatedCourses])

  if (
    !courseSlug ||
    relatedLoading ||
    coursesLoading ||
    relatedError ||
    coursesError
  ) {
    return null
  }

  return (
    <TrackingContainer
      onFirstVisible={() => trackRelatedCoursesCarouselViewed()}
    >
      <section id='related-classes' className='container mc-py-10'>
        <GlobalCarousel
          carouselSlug={courseSlug}
          tiles={relatedCourses}
          location={LOCATION_RELATED_COURSES}
          title={
            <h3 className='mc-text-h3 mc-pl-1 mc-mb-4'>
              {t('membersAlsoLiked.title')}
            </h3>
          }
        />
      </section>
    </TrackingContainer>
  )
}
